import { useState } from "react";
import { ActionIcon, Button, Group, Popover, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { IconCheck, IconPencil, IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { passio } from "../lib/clients";

type Label = "accepted" | "rejected" | "edited";

/**
 * Reports what the user did with a result via POST /napi/feedback, keyed by the original
 * call's X-Request-ID. Shows how customers close the loop on AI output.
 */
export function FeedbackButtons({ requestId, itemRef, compact }: { requestId?: string; itemRef?: string; compact?: boolean }) {
  const [sent, setSent] = useState<Label | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  if (!requestId) return null;

  const send = async (label: Label, n?: string) => {
    setBusy(true);
    try {
      await passio.feedback.send({ requestId, label, itemRef, note: n || undefined, metadata: { source: "passio-demo" } });
      setSent(label);
      setEditOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Group gap={4}>
        <IconCheck size={14} color="var(--pd-green)" />
        <Text fz="xs" c="dimmed">
          Feedback sent ({sent})
        </Text>
      </Group>
    );
  }
  return (
    <Group gap={4} wrap="nowrap">
      {!compact && (
        <Tooltip label="Sends POST /napi/feedback with this call's X-Request-ID. Not billed; it's how the route learns what your users accept." multiline w={260}>
          <Text fz="xs" c="dimmed" mr={2} style={{ cursor: "help", textDecoration: "underline dotted" }}>
            Rate this result
          </Text>
        </Tooltip>
      )}
      <Tooltip label="Accepted">
        <ActionIcon variant="subtle" color="lime.8" loading={busy} onClick={() => send("accepted")} aria-label="Accept result">
          <IconThumbUp size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Rejected">
        <ActionIcon variant="subtle" color="red" loading={busy} onClick={() => send("rejected")} aria-label="Reject result">
          <IconThumbDown size={16} />
        </ActionIcon>
      </Tooltip>
      <Popover opened={editOpen} onChange={setEditOpen} width={280} position="bottom-end" withArrow>
        <Popover.Target>
          <Tooltip label="Edited / corrected">
            <ActionIcon variant="subtle" color="gray" onClick={() => setEditOpen((o) => !o)} aria-label="Report a correction">
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs">
            <Textarea label="What would you change?" placeholder="e.g. it was brown rice, not white" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
            <Button size="xs" onClick={() => send("edited", note)} loading={busy} disabled={!note.trim()}>
              Send correction
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}
